/**
 * **顶部菜单栏**（文件 / 编辑 / 视图）—— 从 `composables/useMenus.ts` 向下拆出来的子模块。
 *
 * ## 为什么单独一个文件
 *
 * `useMenus.ts` 原本是**一个上千行的函数**，三套菜单塞在一起。
 * 这一套是最后一块，也是和另外两套**依赖交集为 0** 的那一块 ——
 * 它读的是「窗口开关 / 工作区 / 包管理 / 导入导出」那一组，
 * 和右键菜单那组几乎不重叠。
 *
 * ## 跟着搬过来的两个东西
 *
 * * `MENUS`（分组定义）—— 只有本文件和 `BarMenuId` 用；
 * * `barMenuId`（当前展开的是哪一组）—— 只有本文件和 `openBarMenu` 用，
 *   而 `openBarMenu` 也在这儿。
 * **这两个不是「共享状态」，是这套菜单自己的状态**，所以跟着走。
 * （`useMenus` 仍然 re-export `MENUS` / `barMenuId`，宿主在用。）
 *
 * ## ⚠️ 保持引用稳定
 *
 * `run: ctx.doUndo`（不包箭头函数）—— 理由同 `contextSections.ts`。
 *
 * ## 归属
 *
 * **向下**：`useMenus.ts` → `menus/barSections.ts`，只有前者引用它。
 */
import { computed, ref } from 'vue'
import type { ComputedRef, Ref } from 'vue'
import { pruneMenu } from '@/types/menu'
import type { MenuSection } from '@/types/menu'
import type { ThemeSummary } from '@/types/theme'
import type { FlowStoreApi } from '@/graph/flowStore'
import type { LayoutState } from '@/layout/areas'
import { WINDOW_LABELS } from '../composables/useAreas'
import type { PackageExample } from '@/node/package'
import type { WindowKey, WindowsView } from '../composables/useAreas'
import type { InstalledPackageView } from '../composables/usePackages'
import type { WorkspaceEntry } from '@/layout/workspaces'
/* ⚠️ 结构类型，不是引擎的类型 —— 换引擎时这一行不用改（`flowTypes.ts`） */
import type { FlowNode as Node } from '@/graph/flowTypes'

/** 菜单栏的分组。顺序就是显示顺序 */
export const MENUS = [
  { id: 'file', label: '文件' },
  { id: 'edit', label: '编辑' },
  { id: 'view', label: '视图' },
  /*
   * ⚠️ 「系统」是**最后加的**，也是**最靠右**的 —— 这是通用约定
   * （关于 / 帮助 / 版本 / 插件这一类"不怎么改数据"的东西都在末尾）。
   *
   * ⚠️ 节点包从「视图」挪过来了：装卸一个节点包改的是**这个工具能做什么**，
   * 不是"内容怎么看"。原来放在视图里，是因为那时只有一个「视图」菜单
   * 能放得下它 —— 有了「系统」之后它才回到自己该在的地方。
   */
  { id: 'system', label: '系统' },
] as const

export type BarMenuId = (typeof MENUS)[number]['id']

/**
 * 这套菜单需要宿主提供的能力 —— 每一项都对应 `MenusCtx` 里的一个字段。
 *
 * ⚠️ 窄化过的：`MenusCtx` 有 67 个字段，这一套只用到 30 个左右。
 */
export interface BarMenuCtx {
  props: { documentId?: string; documentName?: string }
  /**
   * ★ 文档库（2027 加）。
   *
   * ⚠️ 这几个必须是**引用 / ref**（不是渲染时抓一次的值）：
   * 菜单那些 section 是 computed，文档名或"有没有改动"一变，
   * 菜单项要跟着变（`ctx.documentDirty.value` 就在 label 里用着）。
   */
  documentDirty: { value: boolean }
  /** 打开「文档」窗口（没有就分屏开一个） */
  openDocumentsWindow: () => void
  /** 把当前画布存进文档库 */
  saveDocument: () => void
  /** 「系统 → 关于」：开信息弹窗，报版本 / 主题 / 工作区 / 窗口数 */
  openAbout: () => void
  /** 「系统 → 帮助」：开信息弹窗，列快捷键与手势 */
  openHelp: () => void
  /** 切换主题（主题注册表 id） */
  setTheme: (id: string) => void
  /** 全部可选主题 —— 菜单子项**从它生成**，不手写第二份 */
  themes: { value: ThemeSummary[] }
  /** 当前主题 id */
  themeId: { value: string }
  /** 当前主题摘要（菜单项副标题） */
  themeSummary?: ThemeSummary
  /** 开关主题编辑器窗口（它是一个 Area，不是浮层） */
  toggleThemeWindow: () => void
  flowStore: { value: FlowStoreApi | null }
  selectedIds: { value: string[] }
  historyState: { value: { canUndo: boolean; canRedo: boolean; undo?: string; redo?: string } }
  windows: { value: WindowsView }
  layout: { value: LayoutState }
  installedPackages: {
    value: InstalledPackageView[]
  }
  workspaces: { value: WorkspaceEntry[] }
  activeWorkspaceId: { value: string | null }
  toggleWindow: (key: WindowKey) => void
  applyWorkspace: (entry: WorkspaceEntry) => void
  saveWorkspace: (label: string) => string
  deleteWorkspace: (id: string) => void
  importPackage: () => void
  uninstallPackage: (id: string) => void
  enablePackage: (id: string) => void
  exportPackage: (id: string) => void
  /** 打开 / 导入一份 .json（进库 + 打开；UUID 撞车时宿主会弹窗问） */
  openDocumentFile: () => void
  /** ★ 包带来的示例文档（装/卸包时清单跟着变，所以是 ref 形状） */
  examples: { value: PackageExample[] }
  /** 打开一份示例（走的就是打开文件那条路，UUID 撞车照样弹窗） */
  openExample: (id: string) => void
  saveTemplateFile: () => void
  exportDocument: () => void
  exportRenderTree: () => void
  resetGraph: () => void
  openAddMenu: (at?: { x: number; y: number }) => void
  showNotice: (message: string) => void
  doUndo: () => void
  doRedo: () => void
  groupSelection: () => void
  selectAll: () => void
}

/** 当前展开的是哪个菜单栏分组 */
const barMenuId = ref<BarMenuId>('file')

/** 读一下就给你当前该显示的菜单栏分组（只返回展开的那一组） */
export function makeBarSections(ctx: BarMenuCtx): {
  barSections: ComputedRef<MenuSection[]>
  barMenuId: Ref<BarMenuId>
} {
  const barSections = computed<MenuSection[]>(() => {


  /*
   * ⚠️ 写入必须走 `ctx.toggleWindow`（宿主注入的），不能直接改 `ctx.windows.value[key]`。
   *
   * `windows` 现在是**从布局树派生的只读 computed**：直接赋值在严格模式下
   * 会抛错，在非严格模式下会静默丢掉（菜单点了没反应 —— 最难查的那种）。
   * 而且「输出窗口」那一项根本不是翻布尔，是**增删一个 Area**，
   * 只有 `ctx.toggleWindow` 知道这件事。
   *
   * 顺带删掉了两处手写镜像：
   *   `logCollapsed = !windows.problems` → 折叠状态归面板自己，
   *                                        和「窗口在不在」解耦
   */
  const toggle = (id: string, key: WindowKey) => ({
    id,
    label: WINDOW_LABELS[key],
    checked: ctx.windows.value[key],
    run: () => ctx.toggleWindow(key),
  })

  const all = pruneMenu([
    {
      id: 'view',
      title: '视图',
      items: [
          /*
           * ★ 工作区（预设 + 自存）挂在这里，做成**二级菜单**。
           *
           * 用户的要求是两件事并一件：
           *   ·「预设布局也在宽度变化的时候隐藏，在菜单项使用，菜单项永远在」
           *     —— 顶栏那排标签窄屏会隐藏，所以菜单里的入口必须**常驻**；
           *   ·「窗口菜单项去掉，把预设工作区和新建/加载工作区功能，
           *      合并到视图菜单的布局菜单中」。
           *
           * ⚠️ 放在**视图**而不是窗口：换工作区改的是「怎么看这些内容」，
           * 不是「有哪几个窗口」。窗口菜单管单个 Region 的开关。
           */
        {
          id: 'layout',
          label: '布局',
          keywords: ['布局', 'layout', '工作区', 'workspace', '预设', 'preset'],
          hint: ctx.activeWorkspaceId.value
            ? (ctx.workspaces.value.find((w) => w.id === ctx.activeWorkspaceId.value)?.label ?? '')
            : '自定义',
          children: [
            {
              id: 'layout-list',
              title: '切换到',
              titleStyle: 'divider' as const,
              items: ctx.workspaces.value.map((entry) => ({
                id: `layout-${entry.id}`,
                label: entry.label,
                hint: entry.hint,
                keywords: ['布局', 'layout', '预设', 'preset', 'workspace', entry.id],
                checked: ctx.activeWorkspaceId.value === entry.id,
                run: () => ctx.applyWorkspace(entry),
              })),
            },
            {
              id: 'layout-manage',
              title: '管理',
              titleStyle: 'divider' as const,
              items: [
                {
                  id: 'layout-save',
                  label: '把当前排布存为新工作区…',
                  keywords: ['保存', 'save', '新建', '工作区', 'workspace'],
                  run: () => {
                    const label = prompt('工作区名称', `工作区 ${ctx.workspaces.value.length + 1}`)
                    if (label === null) return
                    ctx.saveWorkspace(label)
                  },
                },
                {
                  id: 'layout-reset',
                  label: '恢复当前工作区的默认排布',
                  disabled: ctx.activeWorkspaceId.value === null,
                  keywords: ['重置', 'reset', '恢复', '默认'],
                  run: () => {
                    const entry = ctx.workspaces.value.find((w) => w.id === ctx.activeWorkspaceId.value)
                    if (entry) ctx.applyWorkspace(entry)
                  },
                },
                {
                  id: 'layout-delete',
                  label: '删除当前工作区',
                  danger: true,
                  /* 内置预设删不掉 —— 它们跟着代码走 */
                  disabled: !ctx.workspaces.value.find(
                    (w) => w.id === ctx.activeWorkspaceId.value,
                  )?.removable,
                  keywords: ['删除', 'delete', '工作区'],
                  run: () => {
                    const id = ctx.activeWorkspaceId.value
                    if (!id) return
                    const entry = ctx.workspaces.value.find((w) => w.id === id)
                    if (!entry?.removable) return
                    if (!confirm(`删除工作区「${entry.label}」？当前排布不会变。`)) return
                    ctx.deleteWorkspace(id)
                  },
                },
              ],
            },
          ],
        },
        /*
         * ⚠️ 这里原来有一项「输出窗口」（点一下增删一个输出 Area）。
         * **删掉了** —— 实测反馈：「视图菜单里去掉直接打开窗口的菜单项」。
         *
         * 「视图」这个菜单名管的是**怎么看**（主题、重置视图、全选…），
         * 而"多开一个窗口"改的是**工作区结构**，两者不是一类东西。
         *
         * ⚠️ 功能没丢：每个窗口的 header 右边有个「⋯」，那里面就有
         * 分屏 / 关闭；而"开一个输出窗口"走的是**窗口类型菜单**
         * （点 header 左上角的图标 → 选「输出」）—— 那才是最直接的说法：
         * 「我想在这儿看输出」，而不是「请帮我新增一个输出窗口」。
         *
         * ⚠️ 主题那一项**留着**：它不是一个独立的窗口开关，而是
         * 「主题」这个功能的子功能跳转（换主题 / 打开主题编辑器），
         * 挂在主题子树里是对的。
         */
        /*
         * ── 主题 ────────────────────────────────────────────────
         *
         * ⚠️ 从「一个『浅色主题』勾选项」变成了一棵子树。
         *
         * 旧写法的问题是**它把主题系统锁死在两个值上**：一个布尔，
         * 只能表达「深 / 浅」。想加第三套观感就得再加一个布尔 ——
         * 组合爆炸，而且没有地方放「这份主题动了哪些令牌」这种信息。
         *
         * 现在子项由**主题注册表**生成（内置 + 用户主题），
         * 加一套主题 = 往注册表里放一份数据，这里一行都不用改。
         *
         * ⚠️ 子项必须**从注册表生成**，不能在模板里手写第二份 ——
         * 这个仓库为「同一份清单存在两处、改一处忘一处」记过好几条。
         */
        {
          id: 'theme',
          label: '主题',
          hint: ctx.themeSummary?.name,
          keywords: ['主题', 'theme', '皮肤', 'skin', '配色', '外观'],
          children: [
            {
              id: 'theme-pick',
              title: '选择主题',
              items: ctx.themes.value.map((t) => ({
                id: `theme-${t.id}`,
                label: t.name,
                hint: t.hint,
                checked: ctx.themeId.value === t.id,
                keywords: [t.id, t.scheme === 'light' ? '浅色' : '深色'],
                run: () => ctx.setTheme(t.id),
              })),
            },
            {
              id: 'theme-more',
              items: [
                {
                  id: 'theme-panel',
                  /*
                   * ⚠️ 文案是「窗口」不是「…」。
                   * 这个条目开的**是一个工作区窗口**（和「输出窗口」同机制），
                   * 而不是弹窗 —— 加省略号会让人以为是模态框。
                   * `checked` 让菜单能显示它现在开着。
                   */
                  label: '主题编辑器窗口',
                  hint: '改配色 / 配方 / 密度 / CSS，可导出',
                  keywords: ['编辑', '自定义', 'custom', 'editor', '窗口'],
                  checked: ctx.windows.value.theme,
                  run: ctx.toggleThemeWindow,
                },
              ],
            },
          ],
        },
        /*
         * ⚠️ 这里短暂地有过一项「框选模式」。**删掉了** ——
         * 实测反馈：「不需要在视图菜单里有框选模式菜单项」。
         *
         * 窗口 header 上那个手按钮本身就是这个开关：
         * **亮 = 移动，灭 = 框选**。同一件事有两个入口，迟早会漂
         * （而且菜单里那份还多一次点击）。
         */
        {
          id: 'reset-view',
          label: '重置视图',
          run: () => ctx.flowStore.value?.fitView?.({ padding: 0.3, duration: 240 }),
        },
        { id: 'select-all', label: '全选', hint: 'Ctrl+A', run: ctx.selectAll },
      ],
    },
    {
      /*
       * ⚠️ 「系统」—— 关于这个工具**本身**的东西：能力（节点包）、
       * 说明（帮助）、版本（关于）。它们都不改图里的数据。
       *
       * ⚠️ 节点包是**从「视图」搬过来的**。装卸一个包改的是
       * 「这个工具能做什么」，不是「内容怎么看」；原来放视图里，
       * 只是因为那时没有更合适的地方。
       */
      id: 'system',
      title: '系统',
      items: [
        {
          /*
           * ★ 节点包 —— 装卸「可选加载的领域能力」。
           *
           * ⚠️ 每一项的文案都写清**点下去会发生什么**（卸载「函数」/ 启用…），
           * 而不是只列个名字让人猜。这个菜单是**破坏性**的入口，
           * 含糊的文案会让人不敢点，或者误点。
           */
          id: 'packages',
          label: '节点包',
          keywords: ['包', 'package', '插件', 'plugin', '扩展', '节点包'],
          /*
           * ⚠️ 只数**可选包**（`optional`）：`base` / `core` 是随程序来的，
           * 把它们算进"已启用"会让这个数字永远减不下去 ——
           * 用户看这个数是想知道"我额外装了几个"。
           */
          hint: `${ctx.installedPackages.value.filter((p) => p.optional && p.enabled).length} 个可选包已启用`,
          children: [
            {
              id: 'packages-import',
              title: '导入',
              titleStyle: 'divider' as const,
              items: [
                {
                  id: 'packages-import-file',
                  label: '导入节点包…',
                  keywords: ['导入', 'import', '安装', 'install'],
                  run: () => ctx.importPackage(),
                },
              ],
            },
            {
              id: 'packages-installed',
              title: '已启用',
              titleStyle: 'divider' as const,
              items: ctx.installedPackages.value
                /* ⚠️ 只列**能卸的**（`domain`）—— 基础包在下面那一栏只读列出 */
                .filter((p) => p.optional && p.enabled)
                .map((p) => ({
                  id: `packages-off-${p.id}`,
                  label: `卸载「${p.label}」`,
                  hint: `${p.nodeTypes} 个节点`,
                  keywords: ['卸载', 'uninstall', '关闭', p.id],
                  run: () => ctx.uninstallPackage(p.id),
                })),
            },
            {
              id: 'packages-disabled',
              title: '已卸载',
              titleStyle: 'divider' as const,
              items: ctx.installedPackages.value
                .filter((p) => p.optional && !p.enabled)
                .map((p) => ({
                  id: `packages-on-${p.id}`,
                  label: `启用「${p.label}」`,
                  hint: `${p.nodeTypes} 个节点`,
                  keywords: ['启用', 'enable', '打开', p.id],
                  run: () => ctx.enablePackage(p.id),
                })),
            },
            {
              /*
               * ★ **核心基础：随程序来的，不可卸载**。
               *
               * ⚠️ 用户 2027 定的：函数节点那种**不算"可选加载的包"**，
               * 是编辑器本来就该有的能力。所以这一栏**只列不卸** ——
               * 给一个"卸载"入口等于让用户能把基本功卸掉，
               * 而卸掉之后画布上的节点只会算不出值。
               *
               * ⚠️ 列出来仍然有意义：用户要能看见"这些东西是从哪来的"
               * （而不是以为节点凭空存在），排错时也要知道它跟着程序走。
               */
              id: 'packages-base',
              title: '核心基础（随程序）',
              titleStyle: 'divider' as const,
              items: ctx.installedPackages.value
                .filter((p) => !p.optional)
                .map((p) => ({
                  id: `packages-core-${p.id}`,
                  label: p.label,
                  hint: `${p.nodeTypes} 个节点 · 不可卸载`,
                  keywords: ['核心', '基础', 'core', 'base', p.id],
                  /* 没有 run = 纯展示（菜单模型本来就支持这种条目） */
                })),
            },
            {
              id: 'packages-export',
              title: '导出（当模板改）',
              titleStyle: 'divider' as const,
              items: ctx.installedPackages.value
                /*
                 * ⚠️ 三个条件都要：
                 *   · `optional` —— 基础包跟着程序走，导出成 JSON 改它没有意义；
                 *   · `enabled`  —— 没装的东西没什么可导；
                 *   · `exportable` —— **代码包没有文件形态**（组件 / TS 求值器
                 *     放不进 JSON）。给它一个「导出」入口，点了会静默什么都不发生，
                 *     而用户会以为导出成功了 —— 那比没有入口更坏。
                 */
                .filter((p) => p.optional && p.enabled && p.exportable)
                .map((p) => ({
                  id: `packages-export-${p.id}`,
                  label: `导出「${p.label}」`,
                  hint: p.id,
                  keywords: ['导出', 'export', '另存', p.id],
                  run: () => ctx.exportPackage(p.id),
                })),
            },
          ],
        },
        {
          id: 'help',
          label: '帮助',
          hint: '快捷键与手势一览',
          keywords: ['帮助', 'help', '文档', '说明', '快捷键'],
          /*
           * ⚠️ 走**弹窗**，不是 `showNotice`。
           * 七八条快捷键塞进单行提示条要横着划才看得全，而且几秒就淡出 ——
           * 想照着敲第二个键时已经没了。
           */
          run: () => ctx.openHelp(),
        },
        {
          id: 'about',
          label: '关于',
          keywords: ['关于', 'about', '版本', 'version'],
          run: () => ctx.openAbout(),
        },
      ],
    },
    {
      id: 'edit',
      title: '编辑',
      items: [
        {
          id: 'add-node',
          label: '添加节点…',
          hint: 'Shift+A',
          run: () => ctx.openAddMenu(),
        },
        {
          id: 'group-selection',
          label: '打成组',
          hint: 'Ctrl+G',
          disabled: ctx.selectedIds.value.length === 0,
          run: ctx.groupSelection,
        },
        {
          id: 'undo',
          label: '撤销',
          hint: 'Ctrl+Z',
          disabled: !ctx.historyState.value.canUndo,
          run: ctx.doUndo,
        },
        {
          id: 'redo',
          label: '重做',
          hint: 'Ctrl+Shift+Z',
          disabled: !ctx.historyState.value.canRedo,
          run: ctx.doRedo,
        },
        { id: 'select-all', label: '全选', hint: 'Ctrl+A', run: ctx.selectAll },
      ],
    },
    {
      id: 'file',
      title: '文件',
      items: [
        /*
         * ★ 文档库这一档（2027 加）排在最前面：**"我在编哪一份"是文件菜单
         * 里最常问的问题**，而它原来是隐式的（只活在宿主 props 里）。
         */
        {
          id: 'manage-documents',
          label: '文档管理…',
          keywords: ['documents', '文档', '管理', '首页', 'home', '库', 'library'],
          run: ctx.openDocumentsWindow,
        },
        {
          id: 'save-document',
          label: ctx.documentDirty.value ? '保存到文档库 •（有改动）' : '保存到文档库',
          keywords: ['save', '保存', '文档', 'library'],
          run: ctx.saveDocument,
        },
        { id: 'export-ir', label: '导出 RenderTree（去掉设计数据）', run: ctx.exportRenderTree },
        { id: 'export-doc', label: '导出设计文档（JSON）', run: ctx.exportDocument },
        /*
         * ★★ **示例文件**（2027 加，用户原话："域节点包可以带示例文档，
         * 被自动加载到文件菜单下的示例文件菜单下"）。
         *
         * ⚠️ **清单是生成出来的**：哪个包带了示例、装没装，都反映在这里 ——
         * 一个示例都没有时这一项**整个不出现**（不摆一个永远空着的子菜单）。
         * 这也正是"示例归包管"的好处：打印包卸了，它的示例跟着走。
         */
        ...(ctx.examples.value.length > 0
          ? [
              {
                id: 'examples',
                label: '示例文件',
                keywords: ['example', 'sample', 'demo', '示例', '样例', '例子'],
                children: [
                  {
                    id: 'example-list',
                    items: ctx.examples.value.map((example) => ({
                      id: `example:${example.id}`,
                      label: example.name,
                      ...(example.hint ? { hint: example.hint } : {}),
                      keywords: ['example', 'sample', '示例', '样例', 'demo'],
                      run: () => ctx.openExample(example.id),
                    })),
                  },
                ],
              },
            ]
          : []),
        {
          id: 'save-template',
          label: '保存模板文件…',
          keywords: ['save', '保存', '模板', 'template', 'json'],
          run: ctx.saveTemplateFile,
        },
        {
          /*
           * ⚠️ 只有**一条**"打开文件"的入口了（2027）：打开 = 进库 + 打开，
           * 所以"打开"和"导入到文档库"是同一件事 —— 两个菜单项做同一件事
           * 只会让人猜它们有什么不同。UUID 撞车时宿主弹窗问怎么处理。
           */
          id: 'open-document-file',
          label: '打开文档…',
          keywords: ['open', 'load', 'import', '打开', '载入', '导入', '文档', 'document'],
          run: ctx.openDocumentFile,
        },
        {
          id: 'template-id',
          /*
           * ⚠️ 没传 ID 时**不摆这一项** —— 一个永远显示 `undefined`
           * 的只读项没有意义（原来是硬编码的 `tpl_sf_100x180`）。
           *
           * ⚠️ 2027 起它显示的是**文档库里的当前文档**（名字 + UUID），
           * 不是宿主 props 那一份 —— 在文档窗口里改个名，这里立刻就对。
           */
          label: ctx.props.documentId
            ? `文档：${ctx.props.documentName ?? '未命名'} · ${ctx.props.documentId}`
            : '这份画布还没入库',
          disabled: true,
          keywords: ['id', '文档', 'document', 'uuid'],
        },
        {
          /*
           * ⚠️ 它原来是「重置演示图」—— 重建一张硬编码的示例图。
           * 示例搬走之后唯一诚实的语义是「清空」。
           * 保留 danger + 一次确认：这是唯一一个会**丢掉全部工作**的菜单项。
           */
          id: 'reset-graph',
          label: '清空画布',
          danger: true,
          run: () => {
            if (!confirm('清空画布？所有节点和连线都会删掉（可以撤销）。')) return
            ctx.resetGraph()
            ctx.showNotice('已清空画布')
          },
        },
      ],
    },
  ])

  /*
   * 只显示当前点开的那一组 —— 菜单栏一次只展开一个。
   *
   * ⚠️ 这里踩过一次：布局曾经是独立 section（`id: 'layout'`），
   * 而 `barMenuId` 只可能是 file / edit / view，`'layout'` 永远不等于它们
   * → **那一整组从来没渲染过**（用户以为功能没做，其实只是不可达）。
   * 现在工作区是「视图 → 布局」的**二级菜单**，不再是独立 section，
   * 所以这条过滤回到了它本来的简单语义。
   */
  return all.filter((section) => section.id === barMenuId.value)
  })

  return { barSections, barMenuId }
}
